import { useEffect, useRef, useState } from "react";
import { PaperPlaneRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AskEdujac({
  onNavigate,
  apiPath = "/school/assistant/ask",
  examples = [],
  placeholder = "Ask EDUJAC about attendance, students, fees, exams…",
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    try {
      const { data } = await api.post(apiPath, {
        message: q,
        history: messages.slice(-6).map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages([...next, { role: "assistant", content: data.answer, links: data.links || [], options: data.options || [] }]);
    } catch {
      setMessages([...next, { role: "assistant", content: "Ask EDUJAC is temporarily unavailable. Please try again.", error: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ask-edujac" className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 bg-[#F8F9FB]" data-testid="ask-messages">
        {messages.length === 0 && (
          <div className="py-6">
            <p className="text-sm text-slate-400 text-center mb-4">Try one of these to get started:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {examples.map((ex) => (
                <button
                  key={ex}
                  data-testid={`ask-example-${ex.slice(0, 20).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  onClick={() => ask(ex)}
                  className="px-4 py-2 rounded-full text-xs font-bold bg-white text-slate-600 border border-slate-200 hover:border-brand-coral hover:text-brand-coral transition-colors duration-200"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <p data-testid={`ask-message-${i}`} className="max-w-[85%] bg-brand-coral text-white text-sm font-medium px-4 py-2.5 rounded-2xl rounded-br-md whitespace-pre-wrap">{m.content}</p>
            </div>
          ) : (
            <div key={i} className="flex justify-start">
              <div className="max-w-[92%] space-y-2.5">
                <div data-testid={`ask-message-${i}`} className={`text-sm px-4 py-3 rounded-2xl rounded-bl-md whitespace-pre-wrap leading-relaxed ${m.error ? "bg-status-amber/15 text-amber-800" : "bg-white text-slate-700 border border-slate-100"}`}>
                  {m.content}
                </div>
                {m.options?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pl-1">
                    {m.options.map((o, j) => (
                      <button
                        key={j}
                        data-testid={`ask-option-${i}-${j}`}
                        onClick={() => ask(o.message)}
                        className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-white text-slate-600 border border-slate-200 hover:border-brand-coral hover:text-brand-coral transition-colors duration-200"
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
                {m.links?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pl-1">
                    {m.links.map((l, j) => (
                      <button
                        key={j}
                        data-testid={`ask-nav-${l.tab}`}
                        onClick={() => onNavigate && onNavigate(l.tab)}
                        className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-[#00194E] text-white hover:bg-[#00194E]/90 transition-colors duration-200"
                      >
                        {l.label} →
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        )}
        {busy && <p className="text-sm text-slate-400 pl-1" data-testid="ask-thinking">Ask EDUJAC is thinking…</p>}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2 p-3 border-t border-slate-100 bg-white">
        <Input
          data-testid="ask-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder={placeholder}
          className="bg-white rounded-full px-5"
          disabled={busy}
        />
        <Button data-testid="ask-send-btn" onClick={() => ask()} disabled={busy || !input.trim()} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-5 transition-transform duration-300 active:scale-95">
          <PaperPlaneRight size={16} />
        </Button>
      </div>
    </div>
  );
}
